<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Loading State Overlay -->
    <div v-if="loading" class="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
      <div class="flex flex-col items-center">
        <i class="fas fa-spinner fa-spin text-blue-600 text-4xl"></i>
        <p class="mt-4 text-gray-700 font-medium">Loading Personality...</p>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else-if="personality" class="relative" style="margin-bottom: 50px;">
      <!-- Header Background -->
      <div class="h-48 bg-blue-800 relative">
        <div class="absolute inset-0 bg-gradient-to-br from-blue-900/60 via-blue-800/40 to-indigo-900/50"></div>
        <div class="absolute inset-0 bg-gradient-to-t from-gray-100 to-blue-900/50"></div>
      </div>

      <div class="container mx-auto px-4 -mt-24 relative z-10">
        <!-- Profile Header Card -->
        <div class="bg-white rounded-2xl shadow-xl p-6 flex flex-col items-center text-center">
          <img 
            :src="personality.photo" 
            :alt="personality.name"
            class="w-36 h-36 rounded-full object-cover border-4 border-white -mt-24 shadow-lg"
            @error="handleImageError"
          >
          <h1 class="text-3xl font-bold text-gray-900 mt-4">{{ personality.name }}</h1>
          <h2 class="text-xl text-blue-600 font-medium">{{ personality.title }}</h2>
          
          <!-- Social Media Icons -->
          <div v-if="formattedSocialMedia.length > 0" class="flex flex-wrap justify-center gap-3 mt-4">
            <a 
              v-for="link in formattedSocialMedia"
              :key="link.platform"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="h-10 w-10 flex items-center justify-center rounded-full text-white transition-all duration-300 transform hover:scale-110"
              :class="getSocialClass(link.platform)"
              :title="platformLabel(link.platform)"
            >
              <i :class="getSocialIcon(link.platform)" class="text-lg"></i>
            </a>
          </div>
        </div>

        <!-- Tab Navigation -->
        <div v-if="visibleTabs.length > 0" class="mt-6 bg-white rounded-lg shadow-lg">
          <nav class="flex space-x-2 sm:space-x-4 p-2" aria-label="Tabs">
            <button
              v-for="tab in visibleTabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800',
                'px-3 py-2 font-medium text-sm rounded-md transition-all duration-200'
              ]"
            >
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <!-- Tab Content -->
        <div class="mt-6">
            <!-- Biography -->
            <div v-if="activeTab === 'biography'" class="bg-white rounded-lg shadow-lg p-6 animate-fadeIn">
                <h3 class="text-2xl font-bold text-gray-800 mb-4">Biography</h3>
                <div class="prose max-w-none text-gray-700" v-html="sanitizedBio"></div>
            </div>

            <!-- Career Timeline -->
            <div v-if="activeTab === 'career'" class="bg-white rounded-lg shadow-lg p-6 animate-fadeIn">
                <h3 class="text-2xl font-bold text-gray-800 mb-6">Career Path</h3>
                <div class="relative pl-6 border-l-2 border-blue-200 space-y-8">
                    <div v-for="(item, index) in formattedCareerHistory" :key="index" class="relative">
                        <div class="absolute -left-[35px] top-1 h-4 w-4 rounded-full bg-blue-500 border-2 border-white"></div>
                        <p class="text-sm text-gray-500">{{ formatDate(item.start_date) }} - {{ item.end_date ? formatDate(item.end_date) : 'Present' }}</p>
                        <h4 class="font-semibold text-lg text-gray-800">{{ item.position }}</h4>
                        <p class="text-md text-gray-600">{{ item.organization }}</p>
                        <p v-if="item.description" class="mt-2 text-sm text-gray-500">{{ item.description }}</p>
                    </div>
                </div>
            </div>
            
            <!-- Achievements -->
            <div v-if="activeTab === 'achievements'" class="bg-white rounded-lg shadow-lg p-6 animate-fadeIn">
                <h3 class="text-2xl font-bold text-gray-800 mb-6">Notable Achievements</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div v-for="(item, index) in formattedAchievements" :key="index" class="bg-blue-50 border border-blue-100 rounded-lg p-4">
                         <p v-if="item.year" class="text-sm font-bold text-blue-600">{{ item.year }}</p>
                         <h4 class="font-semibold text-lg text-gray-800 mt-1">{{ item.title }}</h4>
                         <p class="text-sm text-gray-600">{{ item.organization }}</p>
                         <p v-if="item.description" class="mt-2 text-sm text-gray-500">{{ item.description }}</p>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
    
    <!-- Error Display -->
    <div v-else-if="error" class="container mx-auto px-4 py-8 relative z-10">
      <div class="bg-white rounded-lg shadow-md p-6 text-center">
        <i class="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
        <h2 class="text-xl font-bold text-gray-800 mb-2">Could Not Load Personality</h2>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <button @click="loadPersonality" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">Retry</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { format } from 'date-fns';
import { usePublicPersonalityStore } from '@/stores/personalityStore';
import { sanitizeHtml } from '@/utils/sanitize';
const BaseURL = process.env.VUE_APP_API_BASE_URL;

export default {
  name: 'PublicPersonalityDetail',
  props: {
    name: { type: String, required: true }
  },
  data() {
    return {
      personality: null,
      loading: true,
      error: null,
      activeTab: '',
    };
  },
  computed: {
    visibleTabs() {
        if (!this.personality) return [];
        const tabs = [];
        if (this.personality.bio) {
            tabs.push({ id: 'biography', label: 'Biography' });
        }
        if (this.formattedCareerHistory.length > 0) {
            tabs.push({ id: 'career', label: 'Career' });
        }
        if (this.formattedAchievements.length > 0) {
            tabs.push({ id: 'achievements', label: 'Achievements' });
        }
        return tabs;
    },
    formattedCareerHistory() {
      if (!this.personality?.career_history) return [];
      try { return JSON.parse(this.personality.career_history); } catch (e) { return []; }
    },
    formattedAchievements() {
      if (!this.personality?.achievements) return [];
      try { return JSON.parse(this.personality.achievements); } catch (e) { return []; }
    },
    formattedSocialMedia() {
        if (!this.personality?.contacts) return [];
        const platformMap = {
            facebook_url: 'facebook', twitter_url: 'twitter', instagram_url: 'instagram',
            youtube: 'youtube', linkedin_url: 'linkedin', email: 'email', tiktok_url: 'tiktok'
        };
        const validLinks = [];
        for (const key in platformMap) {
            if (this.personality.contacts[key]) {
                const url = key === 'email' ? `mailto:${this.personality.contacts[key]}` : this.personality.contacts[key];
                validLinks.push({ platform: platformMap[key], url });
            }
        }
        return validLinks;
    },
    sanitizedBio() {
      if (!this.personality?.bio) return '';
      return sanitizeHtml(this.personality.bio);
    }
  },
  methods: {
    async loadPersonality(forceRefresh = false) {
      this.loading = true;
      const personalityStore = usePublicPersonalityStore();
      const personalityInStore = personalityStore.currentPersonality;
      const decodedName = decodeURIComponent(this.name);

      // If forcing refresh (e.g. language change), always re-fetch from API
      if (forceRefresh && personalityInStore?.id) {
        await this.fetchPersonalityFromAPI(personalityInStore.id);
        return;
      }

      // 1. Check if a matching personality is already in the store
      if (personalityInStore && personalityInStore.name === decodedName) {
        // 2. Check if the stored data is complete (e.g., has contacts)
        if (personalityInStore.contacts) {
          this.personality = personalityInStore;
          this.initializeTabs();
          this.loading = false;
          return;
        } else {
          await this.fetchPersonalityFromAPI(personalityInStore.id);
        }
      } else {
        await this.fetchPersonalityFromAPI();
      }
    },
    // UPDATED: This method can now fetch by ID (preferred) or by name (fallback)
    async fetchPersonalityFromAPI(personalityId = null) {
      this.error = null;
      const lang = this.$i18n.locale || 'en';
      const config = { headers: { 'Accept-Language': lang } };
      try {
        let personalityDetails;
        if (personalityId) {
          personalityDetails = await axios.get(`${BaseURL}/public-personalities/${personalityId}/`, config);
        } else {
          const personalityName = decodeURIComponent(this.name);
          personalityDetails = await axios.get(`${BaseURL}/public-personalities/by-name/${personalityName}/`, config);
        }
        
        const idForContacts = personalityDetails.data.id;
        const contactResponse = await axios.get(`${BaseURL}/public-personality-contacts/${idForContacts}/`, config).catch(() => ({ data: null }));

        this.personality = {
            ...personalityDetails.data,
            contacts: contactResponse.data
        };
        
        usePublicPersonalityStore().setCurrentPersonality(this.personality);

        this.initializeTabs();
      } catch (err) {
          this.error = 'The requested personality could not be found or there was a network issue.';
      } finally {
          this.loading = false;
      }
    },
    initializeTabs() {
      this.$nextTick(() => {
        if (this.visibleTabs.length > 0) {
          this.activeTab = this.visibleTabs[0].id;
        }
      });
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try { return format(new Date(dateString), 'MMMM yyyy'); } catch (e) { return dateString; }
    },
    getSocialIcon(platform) {
      const icons = {
        facebook: 'fab fa-facebook-f', twitter: 'fab fa-twitter', linkedin: 'fab fa-linkedin-in',
        youtube: 'fab fa-youtube', tiktok: 'fab fa-tiktok', instagram: 'fab fa-instagram',
        email: 'fas fa-envelope',
      };
      return icons[platform] || 'fas fa-share-alt';
    },
     getSocialClass(platform) {
        const classes = {
            facebook: 'bg-blue-600 hover:bg-blue-700', twitter: 'bg-sky-500 hover:bg-sky-600',
            linkedin: 'bg-blue-700 hover:bg-blue-800', youtube: 'bg-red-600 hover:bg-red-700',
            tiktok: 'bg-black hover:bg-gray-800', instagram: 'bg-pink-600 hover:bg-pink-700',
            email: 'bg-gray-600 hover:bg-gray-700'
        };
        return classes[platform] || 'bg-gray-500 hover:bg-gray-600';
    },
    platformLabel(platform) {
      return platform.charAt(0).toUpperCase() + platform.slice(1);
    },
    handleImageError(event) {
      // Consider a more robust path if your assets are processed by a bundler
      event.target.src = '/assets/images/default-avatar.png';
    }
  },
  created() {
    this.loadPersonality();
  },
  watch: {
    // Re-fetch if the route name parameter changes
    name(newName, oldName) {
      if(newName !== oldName) {
        this.loadPersonality();
      }
    },
    // Re-fetch when language changes (force bypass store cache)
    '$i18n.locale'() {
      this.loadPersonality(true);
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.5s ease-in-out;
}
.prose {
  line-height: 1.6;
}
.prose p {
  margin-bottom: 1em;
}
</style>